import { useState } from 'react';
import {
  Plus,
  Search,
  Receipt,
  CheckCircle,
  X,
} from 'lucide-react';
import {
  useInvoices,
  useCreateInvoice,
  useMarkInvoicePaid,
  type InvoiceFilters,
  type Invoice,
  type InvoiceStatus,
  type InvoiceLineItem,
} from '../hooks/useInvoices';
import { Badge } from '../components/shared/Badge';
import { EmptyState } from '../components/shared/EmptyState';
import { cn } from '../lib/cn';

interface InvoicesViewProps {
  onNavigate: (view: string, params?: Record<string, string>) => void;
}

const statusLabels: Record<InvoiceStatus, { label: string; color: string }> = {
  draft: { label: 'Draft', color: 'gray' },
  sent: { label: 'Sent', color: 'blue' },
  paid: { label: 'Paid', color: 'green' },
  overdue: { label: 'Overdue', color: 'red' },
  cancelled: { label: 'Cancelled', color: 'slate' },
  void: { label: 'Void', color: 'slate' },
};

const statusTabs: { label: string; value: InvoiceStatus | '' }[] = [
  { label: 'All', value: '' },
  { label: 'Draft', value: 'draft' },
  { label: 'Sent', value: 'sent' },
  { label: 'Overdue', value: 'overdue' },
  { label: 'Paid', value: 'paid' },
];

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

const emptyLineItem = (): Partial<InvoiceLineItem> => ({
  description: '',
  quantity: 1,
  unitPrice: 0,
  total: 0,
});

export function InvoicesView({ onNavigate }: InvoicesViewProps) {
  const [statusFilter, setStatusFilter] = useState<InvoiceStatus | ''>('');
  const [searchText, setSearchText] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ organizationId: '', dueDate: '', notes: '' });
  const [lineItems, setLineItems] = useState<Partial<InvoiceLineItem>[]>([emptyLineItem()]);

  const filters: InvoiceFilters = {
    status: statusFilter || undefined,
    search: searchText || undefined,
    limit: 100,
  };

  const { data, isLoading, error } = useInvoices(filters);
  const createInvoice = useCreateInvoice();
  const markPaid = useMarkInvoicePaid();

  const invoices = data?.invoices ?? [];

  function addLineItem() {
    setLineItems((prev) => [...prev, emptyLineItem()]);
  }

  function removeLineItem(index: number) {
    setLineItems((prev) => prev.filter((_, i) => i !== index));
  }

  function updateLineItem(index: number, field: string, value: string | number) {
    setLineItems((prev) =>
      prev.map((item, i) => {
        if (i !== index) return item;
        const updated = { ...item, [field]: value };
        if (field === 'quantity' || field === 'unitPrice') {
          updated.total = (Number(updated.quantity) || 0) * (Number(updated.unitPrice) || 0);
        }
        return updated;
      }),
    );
  }

  function handleCreate() {
    const items = lineItems
      .filter((item) => item.description)
      .map((item) => ({
        ...item,
        quantity: Number(item.quantity) || 1,
        unitPrice: Number(item.unitPrice) || 0,
        total: (Number(item.quantity) || 1) * (Number(item.unitPrice) || 0),
      }));

    createInvoice.mutate(
      {
        organizationId: formData.organizationId || undefined,
        dueDate: formData.dueDate,
        notes: formData.notes || undefined,
        items: items as InvoiceLineItem[],
      },
      {
        onSuccess: () => {
          setShowForm(false);
          setFormData({ organizationId: '', dueDate: '', notes: '' });
          setLineItems([emptyLineItem()]);
        },
      },
    );
  }

  return (
    <div className="flex-1 overflow-y-auto">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Invoices</h1>
          <p className="mt-0.5 text-sm text-slate-500">
            {data ? `${data.total} invoice${data.total !== 1 ? 's' : ''}` : 'Invoice tracking'}
          </p>
        </div>
        <button className="btn-primary" onClick={() => setShowForm(true)}>
          <Plus className="h-4 w-4" />
          New Invoice
        </button>
      </div>

      {/* Filter Bar */}
      <div className="border-b border-surface-200 bg-white px-6 py-3">
        <div className="flex items-center gap-3">
          <div className="flex rounded-lg border border-surface-200 p-0.5">
            {statusTabs.map((tab) => (
              <button
                key={tab.value}
                className={cn(
                  'rounded-md px-3 py-1 text-sm font-medium transition-colors',
                  statusFilter === tab.value ? 'bg-brand-50 text-brand-700' : 'text-slate-600 hover:text-slate-900',
                )}
                onClick={() => setStatusFilter(tab.value)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <form onSubmit={(e) => e.preventDefault()} className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input type="text" value={searchText} onChange={(e) => setSearchText(e.target.value)} placeholder="Search invoices..." className="input pl-9" />
          </form>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {error ? (
          <div className="card p-8 text-center text-sm text-red-600">Failed to load invoices.</div>
        ) : isLoading ? (
          <div className="card">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-center gap-4 border-b border-surface-100 px-4 py-3 last:border-0">
                <div className="h-4 w-32 animate-pulse rounded bg-surface-200" />
                <div className="flex-1" />
                <div className="h-4 w-20 animate-pulse rounded bg-surface-200" />
              </div>
            ))}
          </div>
        ) : invoices.length > 0 ? (
          <div className="card overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-surface-200 bg-surface-50">
                  <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Invoice #</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Organization</th>
                  <th className="px-4 py-2.5 text-center text-xs font-semibold uppercase tracking-wider text-slate-500">Status</th>
                  <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Total</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Due Date</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Paid Date</th>
                  <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-100">
                {invoices.map((inv) => {
                  const sCfg = statusLabels[inv.status];
                  return (
                    <tr key={inv.id} className="hover:bg-surface-50">
                      <td className="px-4 py-3 text-sm font-medium text-slate-900">{inv.invoiceNumber}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{inv.organizationName ?? '--'}</td>
                      <td className="px-4 py-3 text-center"><Badge color={sCfg.color} size="sm">{sCfg.label}</Badge></td>
                      <td className="px-4 py-3 text-right text-sm font-medium text-slate-900">{formatCurrency(inv.total)}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{new Date(inv.dueDate).toLocaleDateString()}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{inv.paidAt ? new Date(inv.paidAt).toLocaleDateString() : '--'}</td>
                      <td className="px-4 py-3 text-right">
                        {(inv.status === 'sent' || inv.status === 'overdue') && (
                          <button
                            className="btn-secondary btn-sm text-green-600"
                            onClick={() => markPaid.mutate({ id: inv.id })}
                            disabled={markPaid.isPending}
                            title="Mark Paid"
                          >
                            <CheckCircle className="h-3.5 w-3.5" />
                            Paid
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState
            icon={Receipt}
            title="No invoices yet"
            description="Create an invoice to track payments."
            actionLabel="New Invoice"
            onAction={() => setShowForm(true)}
          />
        )}
      </div>

      {/* Create Invoice Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="card w-full max-w-2xl p-6 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-900">New Invoice</h2>
              <button onClick={() => setShowForm(false)} className="text-slate-400 hover:text-slate-600">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label mb-1">Organization ID</label>
                  <input className="input" value={formData.organizationId} onChange={(e) => setFormData((f) => ({ ...f, organizationId: e.target.value }))} />
                </div>
                <div>
                  <label className="label mb-1">Due Date</label>
                  <input className="input" type="date" value={formData.dueDate} onChange={(e) => setFormData((f) => ({ ...f, dueDate: e.target.value }))} />
                </div>
              </div>

              {/* Line Items */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="label">Line Items</label>
                  <button className="btn-secondary btn-sm" onClick={addLineItem}><Plus className="h-3 w-3" /> Add</button>
                </div>
                <div className="space-y-2">
                  {lineItems.map((item, i) => (
                    <div key={i} className="flex items-center gap-2 rounded-lg border border-surface-200 p-2">
                      <input className="input flex-1" placeholder="Description" value={item.description ?? ''} onChange={(e) => updateLineItem(i, 'description', e.target.value)} />
                      <input className="input w-20" type="number" placeholder="Qty" value={item.quantity ?? ''} onChange={(e) => updateLineItem(i, 'quantity', e.target.value)} />
                      <input className="input w-28" type="number" placeholder="Unit Price" value={item.unitPrice ?? ''} onChange={(e) => updateLineItem(i, 'unitPrice', e.target.value)} />
                      <span className="text-sm font-medium text-slate-600 w-24 text-right">{formatCurrency((Number(item.quantity) || 0) * (Number(item.unitPrice) || 0))}</span>
                      {lineItems.length > 1 && (
                        <button className="text-red-400 hover:text-red-600" onClick={() => removeLineItem(i)}>
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label className="label mb-1">Notes</label>
                <textarea className="input" rows={2} value={formData.notes} onChange={(e) => setFormData((f) => ({ ...f, notes: e.target.value }))} />
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button className="btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
              <button className="btn-primary" onClick={handleCreate} disabled={createInvoice.isPending}>
                {createInvoice.isPending ? 'Creating...' : 'Create Invoice'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
